import {Component, effect, inject, OnInit, signal, Signal} from '@angular/core';
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {IEnableStockItemRule, SettingsReferenceEnum, TLineColour} from '../../models';
import {
  selectSelectedSetting,
  selectSelectedStoreEnableItemRule,
  selectSelectedStoreLineColours,
} from '../../store/setting.selectors';
import {IUser} from '../../../user-access/models';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {getFieldErrorString} from '../../../../shared/utils/form/form.utils';
import {settingsSetSelected, settingStockItemEnableRules} from '../../store/setting.actions';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {ICrudOptions, IUserAccessFeatureStockRules} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureStockRulesForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {
  decodeOnHoldCode,
  getAllOnHoldOptions,
} from '../../../../shared/shared-module-ui/utils/on-hold-code/on-hold-code.utils';

@Component({
  selector: 'app-feature-stock-rule-enable',
  templateUrl: './feature-stock-rule-enable.component.html',
  styleUrl: './feature-stock-rule-enable.component.scss',
  standalone: false,
})
export class FeatureStockRuleEnableComponent implements OnInit {

  //#region INJECTORS
  private formBuilder = inject(FormBuilder);
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  //#endregion

  //#region PROPERTIES
  formEnableItemRule: FormGroup;

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _enableItemRule: Signal<IEnableStockItemRule> = this._store.selectSignal(selectSelectedStoreEnableItemRule);
  private readonly _lineColours: Signal<TLineColour> = this._store.selectSignal(selectSelectedStoreLineColours);
  private readonly _user: Signal<IUser> = this._store.selectSignal(selectUser);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _uaStockRules: Signal<IUserAccessFeatureStockRules> = this._store.selectSignal(selectUserAccessFeatureStockRulesForSelectedStore);

  protected descPrefixArray = signal<string[]>([]);
  protected descSuffixArray = signal<string[]>([]);
  protected descPrefixSelected = signal<string[]>([]);
  protected descSuffixSelected = signal<string[]>([]);
  protected onHoldCodeSelected = signal<string[]>([]);

  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const userAccess = this._uaStockRules();
      const enableItemRule = this._enableItemRule();
      this.initializeForms(userAccess, enableItemRule);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
  }

  //#endregion

  //#region ACCESSOR - ENABLE ITEM RULE SETTINGS

  get hasEnableItemRuleBeenEdited(): boolean {
    return this.formEnableItemRule?.dirty ?? false;
  }

  get hasEnableItemRuleErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string; }[] = [];
    const descPrefixErrors = this.formEnableItemRule?.controls['descPrefix']?.errors;
    const descSuffixErrors = this.formEnableItemRule?.controls['descSuffix']?.errors;
    const lineColourErrors = this.formEnableItemRule?.controls['lineColour']?.errors;
    const onHoldCodeErrors = this.formEnableItemRule?.controls['onHoldCode']?.errors;

    if (descPrefixErrors) {
      errors.push({
        errors: descPrefixErrors,
        error: getFieldErrorString(descPrefixErrors, 'descPrefix'),
        field: 'descPrefix',
      });
    }
    if (descSuffixErrors) {
      errors.push({
        errors: descSuffixErrors,
        error: getFieldErrorString(descSuffixErrors, 'descSuffix'),
        field: 'descSuffix',
      });
    }
    if (lineColourErrors) {
      errors.push({
        errors: lineColourErrors,
        error: getFieldErrorString(lineColourErrors, 'lineColour'),
        field: 'lineColour',
      });
    }
    if (onHoldCodeErrors) {
      errors.push({
        errors: onHoldCodeErrors,
        error: getFieldErrorString(onHoldCodeErrors, 'onHoldCode'),
        field: 'onHoldCode',
      });
    }
    return errors;
  }

  get isEnableItemRuleSubmitDisabled(): boolean {
    return (this.canEnableItemRuleBeEdited === false) || (this.canEnableItemRuleBeEdited === true && this.hasEnableItemRuleBeenEdited === false) || (this.formEnableItemRule?.invalid ?? true);
  }

  get canEnableItemRuleBeEdited(): boolean {
    const access = this.stockManagerUserAccess?.enable;
    return access?.canEdit ?? false;
  }

  get canEnableItemRuleBeSubmitted(): boolean {
    const access = this.stockManagerUserAccess?.enable;
    return access?.canSubmit ?? false;
  }

  get canEnableItemRuleBeViewed(): boolean {
    const access = this.stockManagerUserAccess?.enable;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - USER SETTINGS + USER ACCESS + STORE
  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get userSettings(): IUser {
    return this._user();
  }

  get stockManagerUserAccess(): IUserAccessFeatureStockRules {
    return this._uaStockRules();
  }

  //#endregion

  //#region ACCESSOR - GLOBALS
  get haveAnyEditsBeenMade(): boolean {
    return (this.formEnableItemRule?.dirty ?? false);
  }

  get enableItemRule(): IEnableStockItemRule {
    return this._enableItemRule();
  }

  //#endregion

  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.formEnableItemRule = this.formBuilder.group({});
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
        this.revertEnableItemRuleDetails();
        break;
      case 'enableItemRule':
        this.revertEnableItemRuleDetails();
        break;
    }
  }

  async onSubmitForm(form: string): Promise<void> {
    // Check for deselected prefixes and suffixes
    const currentPrefixArray = this.enableItemRule?.descPrefixArray ?? [];
    const currentSuffixArray = this.enableItemRule?.descSuffixArray ?? [];
    const nextPrefixArray = this.descPrefixSelected();
    const nextSuffixArray = this.descSuffixSelected();

    const deselectedPrefixes = currentPrefixArray.filter((item: string) => !nextPrefixArray.includes(item));
    const deselectedSuffixes = currentSuffixArray.filter((item: string) => !nextSuffixArray.includes(item));

    // If there are deselected items, show confirmation modal
    if (deselectedPrefixes.length > 0 || deselectedSuffixes.length > 0) {
      const shouldProceed = await this.showDeselectionWarningModal(deselectedPrefixes, deselectedSuffixes);
      if (!shouldProceed) {
        return;
      }
    }

    let data: Partial<IEnableStockItemRule> = {...this.enableItemRule};
    switch (form) {
      case 'all':
        data = {
          ...data,
          ...this.generateEnableItemRulePatch(data),
        };
        break;
      case 'enableItemRule':
        if (!this.canEnableItemRuleBeSubmitted) break;
        data = {...data, ...this.generateEnableItemRulePatch(data)};
        break;
      default:
        break;
    }
    this._store.dispatch(settingStockItemEnableRules.setEnableStockItemRule({settings: data as IEnableStockItemRule}));
    this.formEnableItemRule.markAsPristine();
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessFeatureStockRules, rule: IEnableStockItemRule): void {
    this.buildEnableItemRuleForm(access?.enable, rule);
  }

  private buildEnableItemRuleForm(access: ICrudOptions, rule: IEnableStockItemRule): void {
    if (access?.canView && this.formEnableItemRule.pristine) {
      const {descPrefix, descSuffix, descPrefixArray, descSuffixArray, lineColour, onHoldCode} = rule;
      const tempForm = this.formBuilder.group({});

      this.addFormControl(tempForm, 'descPrefix', descPrefix ?? '', false, [Validators.maxLength(50)]);
      this.addFormControl(tempForm, 'descSuffix', descSuffix ?? '', false, [Validators.maxLength(50)]);
      this.addFormControl(tempForm, 'lineColour', lineColour ?? 0, false, [Validators.min(0), Validators.max(99)]);
      this.addFormControl(tempForm, 'onHoldCode', onHoldCode ?? 0, false, [Validators.min(0)]);

      this.descPrefixArray.set(descPrefixArray ?? []);
      this.descSuffixArray.set(descSuffixArray ?? []);
      this.descPrefixSelected.set(descPrefixArray ?? []);
      this.descSuffixSelected.set(descSuffixArray ?? []);

      const decodedCodes = decodeOnHoldCode(onHoldCode ?? 0, {returnCodes: true});
      const prefixedCodes = decodedCodes.map((code: string) => `ohc-${code}`);
      this.onHoldCodeSelected.set(prefixedCodes);

      tempForm.markAsPristine();
      this.formEnableItemRule = tempForm;
    }
  }

  private addFormControl<T = unknown>(form: FormGroup, controlName: string, value: T, disabled: boolean, validators: ValidatorFn[] = []): void {
    form.addControl(controlName, this.formBuilder.control({value, disabled}, validators));
  }

  //#endregion

  //#region FORM - ON CLICK
  onPrefixChange(selected: { [key: string]: ISelectOption }): void {
    const allKeys = Object.keys(selected);
    const selectedKeys = allKeys.filter((key: string) => selected[key].selected);

    this.descPrefixArray.update((current: string[]) => {
      const newItems = allKeys.filter((key: string) => !current.includes(key));
      return [...current, ...newItems];
    });

    this.descPrefixSelected.set(selectedKeys);
    this.formEnableItemRule.markAsDirty();
  }

  onSuffixChange(selected: { [key: string]: ISelectOption }): void {
    const allKeys = Object.keys(selected);
    const selectedKeys = allKeys.filter((key: string) => selected[key].selected);

    this.descSuffixArray.update((current: string[]) => {
      const newItems = allKeys.filter((key: string) => !current.includes(key));
      return [...current, ...newItems];
    });

    this.descSuffixSelected.set(selectedKeys);
    this.formEnableItemRule.markAsDirty();
  }

  onLineColourChange(selected: { [key: string]: ISelectOption }): void {
    const selection = Object.keys(selected).filter((option: string) => selected[option].selected);
    const selectedKey: string = selection?.length > 0 ? selection[0] : 'lc-0';
    const lineColourCode = parseInt(selectedKey.replace('lc-', ''), 10);
    this.formEnableItemRule.controls['lineColour'].setValue(lineColourCode);
    this.formEnableItemRule.markAsDirty();
  }

  onHoldCodeChange(selected: { [key: string]: ISelectOption }): void {
    const selectedKeys = Object.keys(selected).filter((key: string) => selected[key].selected);
    const selectedCodes = selectedKeys.map((key: string) => parseInt(key.replace('ohc-', ''), 10));
    const encodedValue = selectedCodes.reduce((sum: number, code: number) => sum + code, 0);

    this.onHoldCodeSelected.set(selectedKeys);
    this.formEnableItemRule.controls['onHoldCode'].setValue(encodedValue);
    this.formEnableItemRule.markAsDirty();
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  private revertEnableItemRuleDetails(): void {
    const rule = this.enableItemRule;
    const {descPrefix, descSuffix, descPrefixArray, descSuffixArray, lineColour, onHoldCode} = rule;

    this.formEnableItemRule.reset({
      descPrefix: descPrefix ?? '',
      descSuffix: descSuffix ?? '',
      lineColour: lineColour ?? 0,
      onHoldCode: onHoldCode ?? 0,
    });

    this.descPrefixArray.set(descPrefixArray ?? []);
    this.descSuffixArray.set(descSuffixArray ?? []);
    this.descPrefixSelected.set(descPrefixArray ?? []);
    this.descSuffixSelected.set(descSuffixArray ?? []);

    const decodedCodes = decodeOnHoldCode(onHoldCode ?? 0, {returnCodes: true});
    const prefixedCodes = decodedCodes.map((code: string) => `ohc-${code}`);
    this.onHoldCodeSelected.set(prefixedCodes);
  }

  //#endregion

  //#region FORM - GENERATE PATCH DATA

  private generateEnableItemRulePatch(data: Partial<IEnableStockItemRule>): Partial<IEnableStockItemRule> {
    const formValues = this.formEnableItemRule.getRawValue();
    const currentDescPrefix = this.enableItemRule?.descPrefix?.trim();
    const currentDescSuffix = this.enableItemRule?.descSuffix?.trim();
    const currentDescPrefixArray = this.enableItemRule?.descPrefixArray ?? [];
    const currentDescSuffixArray = this.enableItemRule?.descSuffixArray ?? [];
    const currentLineColour = this.enableItemRule?.lineColour;
    const currentOnHoldCode = this.enableItemRule?.onHoldCode;

    const nextDescPrefix = formValues?.descPrefix?.trim();
    const nextDescSuffix = formValues?.descSuffix?.trim();
    const nextDescPrefixArray = this.descPrefixSelected();
    const nextDescSuffixArray = this.descSuffixSelected();
    const nextLineColour = formValues?.lineColour;
    const nextOnHoldCode = formValues?.onHoldCode;

    const prefixArrayChanged = JSON.stringify([...currentDescPrefixArray].sort()) !== JSON.stringify([...nextDescPrefixArray].sort());
    const suffixArrayChanged = JSON.stringify([...currentDescSuffixArray].sort()) !== JSON.stringify([...nextDescSuffixArray].sort());

    const rulePatch: Partial<IEnableStockItemRule> = {
      ...(currentDescPrefix !== nextDescPrefix && {descPrefix: nextDescPrefix}),
      ...(currentDescSuffix !== nextDescSuffix && {descSuffix: nextDescSuffix}),
      ...(prefixArrayChanged && {descPrefixArray: nextDescPrefixArray}),
      ...(suffixArrayChanged && {descSuffixArray: nextDescSuffixArray}),
      ...(currentLineColour !== nextLineColour && {lineColour: nextLineColour}),
      ...(currentOnHoldCode !== nextOnHoldCode && {onHoldCode: nextOnHoldCode}),
    };

    if (!(Object.keys(rulePatch).length > 0)) return data;
    data = {...data, ...rulePatch};
    return data;
  }

  //#endregion

  //#region SELECT - OPTIONS

  getLineColourOptions(selectedCode: number | string | null | undefined): Record<string, ISelectOption> {
    const lineColours = this._lineColours();
    const selectedKey = `lc-${selectedCode ?? 0}`;

    const allLineColours = [
      {
        code: '0',
        description: 'No Line Colour',
        value: 'transparent',
      },
      ...Object.entries(lineColours).map(([key, value]) => ({
        code: key,
        description: value.description,
        value: value.value,
      })),
    ];

    const sortedLineColours = allLineColours.sort((a, b) =>
      a.description.localeCompare(b.description),
    );

    const options: Record<string, ISelectOption> = {};

    sortedLineColours.forEach((lineColour) => {
      const key = `lc-${lineColour.code}`;
      options[key] = {
        name: `${lineColour.description} (${lineColour.code})`,
        selected: key === selectedKey,
        disabled: false,
        colour: lineColour.value,
      };
    });

    return options;
  }

  getOnHoldCodeOptions(selectedKeys: string[]): Record<string, ISelectOption> {
    const onHoldOptions = getAllOnHoldOptions(false);
    const selected = selectedKeys ?? [];

    const sortedOptions = onHoldOptions
      .sort((a: { code: number; label: string }, b: { code: number; label: string }) =>
        a.label.localeCompare(b.label));

    const options: Record<string, ISelectOption> = {};

    sortedOptions.forEach((option: { code: number; label: string }) => {
      const key = `ohc-${option.code}`;
      options[key] = {
        name: option.label,
        selected: selected.includes(key),
        disabled: false,
      };
    });

    return options;
  }

  getPrefixOptions(): Record<string, ISelectOption> {
    const options: Record<string, ISelectOption> = {};
    const prefixes = this.descPrefixArray();
    const selected = this.descPrefixSelected();
    prefixes.forEach((prefix: string) => {
      options[prefix] = {
        name: prefix,
        selected: selected.includes(prefix),
        disabled: false,
      };
    });
    return options;
  }

  getSuffixOptions(): Record<string, ISelectOption> {
    const options: Record<string, ISelectOption> = {};
    const suffixes = this.descSuffixArray();
    const selected = this.descSuffixSelected();
    suffixes.forEach((suffix: string) => {
      options[suffix] = {
        name: suffix,
        selected: selected.includes(suffix),
        disabled: false,
      };
    });
    return options;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  private async onShowModalUnsavedChanges(ref: SettingsReferenceEnum): Promise<void> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    await this.modalUtils.createModal(modalProps).then((result: IModalDismissResult) => {
      this._store.dispatch(settingsSetSelected.setSelectedReference({ref: SettingsReferenceEnum.accountUserProfile}));
      if (result.role === 'modal-accept') {
        this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
      }
    });
  }

  //#endregion

  //#region MODAL - DESELECTION WARNING
  private async showDeselectionWarningModal(deselectedPrefixes: string[], deselectedSuffixes: string[]): Promise<boolean> {
    const contextDescription: string[] = [];

    if (deselectedPrefixes.length > 0) {
      contextDescription.push('The following prefixes will be removed:');
      contextDescription.push(...deselectedPrefixes.map((prefix: string) => `• ${prefix}`));
    }

    if (deselectedSuffixes.length > 0) {
      if (deselectedPrefixes.length > 0) {
        contextDescription.push('');
      }
      contextDescription.push('The following suffixes will be removed:');
      contextDescription.push(...deselectedSuffixes.map((suffix: string) => `• ${suffix}`));
    }

    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Proceed',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Warning: Items Will Be Removed',
      modalTitleIcon: IconsEnum.closeCircleOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'You have deselected items that will be permanently removed.',
      contextDescription,
      cssClass: ['shared-basic-modal-css-medium'],
    };

    return await this.modalUtils.createModal(modalProps).then((result: IModalDismissResult) => {
      return result.role === 'modal-accept';
    });
  }

  //#endregion

}
